'use client';

import React, { useRef, useState } from 'react';
import { Check, Loader2, Trash2, Upload, X } from 'lucide-react';
import type { BuilderPage } from '@/lib/storefront/section-library';
import type { CustomFontFace, PageDesignTokens, PageSettings } from '@/lib/storefront/page-settings';
import { CORNER_CHOICES, FONT_CHOICES } from '@/lib/storefront/appearance';
import { MediaDropzone } from './MediaDropzone';

type PagePatch = Partial<Pick<BuilderPage, 'name' | 'settings' | 'designTokens'>>;

interface PageSettingsPanelProps {
  page: BuilderPage;
  pageSlug: string;
  uploadUrl: string;
  saving: boolean;
  saveError?: string | null;
  onChange: (patch: PagePatch) => void;
  onSave: () => Promise<boolean>;
  onClose: () => void;
}

type Tab = 'general' | 'seo' | 'design' | 'background';

const TABS: { id: Tab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'seo', label: 'SEO' },
  { id: 'design', label: 'Design' },
  { id: 'background', label: 'Background' },
];

const inputClass =
  'mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink outline-none focus:border-brand';

export function PageSettingsPanel({
  page,
  pageSlug,
  uploadUrl,
  saving,
  saveError,
  onChange,
  onSave,
  onClose,
}: PageSettingsPanelProps) {
  const [tab, setTab] = useState<Tab>('general');
  const [cssError, setCssError] = useState<string | null>(null);
  const [fontError, setFontError] = useState<string | null>(null);
  const [fontBusy, setFontBusy] = useState(false);
  const fontInputRef = useRef<HTMLInputElement>(null);
  const cssInputRef = useRef<HTMLInputElement>(null);
  const settings = page.settings ?? {};
  const tokens = page.designTokens ?? {};
  const customFonts = Array.isArray(tokens.customFonts) ? tokens.customFonts : [];

  const setSettings = (patch: Partial<PageSettings>) => {
    onChange({ settings: { ...settings, ...patch } });
  };
  const setTokens = (patch: Partial<PageDesignTokens>) => {
    onChange({ designTokens: { ...tokens, ...patch } });
  };

  const primary = tokens.colors?.primary ?? '';
  const fontStack = tokens.fonts?.body ?? '';
  const customFamily = customFonts.find((font) => fontStack.includes(`'${font.family}'`) || fontStack.includes(`"${font.family}"`));
  const fontId = customFamily
    ? `custom:${customFamily.family}`
    : FONT_CHOICES.find((choice) => choice.stack === fontStack)?.id ?? 'system';
  const cornerId = CORNER_CHOICES.find((choice) => choice.radii.md === tokens.borderRadius?.md)?.id ?? 'rounded';

  async function uploadFont(file: File) {
    setFontBusy(true);
    setFontError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('kind', 'font');
      const res = await fetch(uploadUrl, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || typeof data.url !== 'string' || typeof data.family !== 'string') {
        throw new Error(typeof data.error === 'string' ? data.error : 'Font upload failed');
      }
      const next: CustomFontFace = {
        family: data.family,
        url: data.url,
        format: typeof data.format === 'string' ? data.format : 'woff2',
      };
      const fonts = [...customFonts.filter((font) => font.family !== next.family), next].slice(0, 8);
      setTokens({
        customFonts: fonts,
        fonts: { ...tokens.fonts, body: `'${next.family}', sans-serif` },
      });
    } catch (error) {
      setFontError(error instanceof Error ? error.message : 'Font upload failed');
    } finally {
      setFontBusy(false);
    }
  }

  function loadCssFile(file: File) {
    setCssError(null);
    if (!file.name.toLowerCase().endsWith('.css') && file.type !== 'text/css') {
      setCssError('Upload a .css file.');
      return;
    }
    if (file.size > 50_000) {
      setCssError('CSS files must be 50 KB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      if (text.length > 8000) {
        setCssError('Custom CSS is longer than 8,000 characters.');
        return;
      }
      setTokens({ customCss: text });
    };
    reader.onerror = () => setCssError('Could not read that CSS file.');
    reader.readAsText(file);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4">
      <div className="dashboard-root flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-card text-ink shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">Page settings</p>
            <h2 className="text-sm font-semibold text-ink">{page.name || 'Untitled page'}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-1.5 text-muted hover:bg-shell hover:text-ink disabled:opacity-40"
            aria-label="Close page settings"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex border-b border-line">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`flex-1 px-3 py-2 text-xs font-semibold ${
                tab === item.id
                  ? 'border-b-2 border-brand text-ink'
                  : 'text-muted hover:text-ink'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="scrollbar-none min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {tab === 'general' && (
            <>
              <label className="block text-xs font-medium text-ink">
                Page name
                <input
                  value={page.name}
                  onChange={(event) => onChange({ name: event.target.value })}
                  className={inputClass}
                  maxLength={100}
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                URL
                <span className="mt-1 flex items-center rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-muted">
                  <span>/</span>
                  <span>{pageSlug === 'home' ? '' : pageSlug}</span>
                </span>
                <span className="mt-1 block text-[11px] font-normal text-muted">
                  The URL is fixed after the page is created. Duplicate the page to use a different URL.
                </span>
              </label>
            </>
          )}

          {tab === 'seo' && (
            <>
              <label className="block text-xs font-medium text-ink">
                Meta title
                <input
                  value={settings.title ?? ''}
                  onChange={(event) => setSettings({ title: event.target.value })}
                  className={inputClass}
                  maxLength={70}
                  placeholder="Fleet rental in Agadir"
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                Meta description
                <textarea
                  value={settings.description ?? ''}
                  onChange={(event) => setSettings({ description: event.target.value })}
                  className={`${inputClass} min-h-[72px]`}
                  maxLength={160}
                  placeholder="A short description shown in search results."
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                Keywords
                <input
                  value={settings.keywords ?? ''}
                  onChange={(event) => setSettings({ keywords: event.target.value })}
                  className={inputClass}
                  maxLength={300}
                  placeholder="car rental, Agadir, airport"
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                Canonical URL
                <input
                  value={settings.canonicalUrl ?? ''}
                  onChange={(event) => setSettings({ canonicalUrl: event.target.value })}
                  className={inputClass}
                  placeholder="https://agency.example/fleet"
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                Social title
                <input
                  value={settings.ogTitle ?? ''}
                  onChange={(event) => setSettings({ ogTitle: event.target.value })}
                  className={inputClass}
                  maxLength={70}
                  placeholder="Defaults to the meta title"
                />
              </label>
              <label className="block text-xs font-medium text-ink">
                Social description
                <textarea
                  value={settings.ogDescription ?? ''}
                  onChange={(event) => setSettings({ ogDescription: event.target.value })}
                  className={`${inputClass} min-h-[72px]`}
                  maxLength={160}
                  placeholder="Defaults to the meta description"
                />
              </label>
              <MediaDropzone
                label="Social image (Open Graph)"
                value={settings.ogImage ?? ''}
                onChange={(value) => setSettings({ ogImage: value })}
                uploadUrl={uploadUrl}
              />
              <div className="space-y-2 rounded-xl border border-line bg-shell/40 p-3">
                <label className="flex items-center gap-2 text-xs font-medium text-ink">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.noIndex)}
                    onChange={(event) => setSettings({ noIndex: event.target.checked })}
                  />
                  Ask search engines not to index this page
                </label>
                <label className="flex items-center gap-2 text-xs font-medium text-ink">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.noFollow)}
                    onChange={(event) => setSettings({ noFollow: event.target.checked })}
                  />
                  Ask search engines not to follow links on this page
                </label>
                <p className="text-[11px] font-normal text-muted">
                  Sitemap and robots.txt are generated per agency at /api/sitemap.xml and /api/robots.txt.
                </p>
              </div>
            </>
          )}

          {tab === 'design' && (
            <>
              <ColorField
                label="Brand color"
                value={primary}
                onChange={(value) => setTokens({ colors: { ...tokens.colors, primary: value } })}
              />
              <label className="block text-xs font-medium text-ink">
                Font
                <select
                  value={fontId}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value.startsWith('custom:')) {
                      const family = value.slice('custom:'.length);
                      setTokens({ fonts: { ...tokens.fonts, body: `'${family}', sans-serif` } });
                      return;
                    }
                    const choice = FONT_CHOICES.find((item) => item.id === value);
                    setTokens({ fonts: { ...tokens.fonts, body: choice?.stack ?? '' } });
                  }}
                  className={inputClass}
                >
                  {FONT_CHOICES.map((choice) => (
                    <option key={choice.id} value={choice.id}>{choice.label}</option>
                  ))}
                  {customFonts.map((font) => (
                    <option key={font.family} value={`custom:${font.family}`}>
                      Uploaded · {font.family}
                    </option>
                  ))}
                </select>
              </label>

              <div className="rounded-xl border border-line bg-shell/40 p-3">
                <p className="text-xs font-semibold text-ink">Upload a custom font</p>
                <p className="mt-1 text-[11px] text-muted">WOFF2, WOFF or TTF · max 2 MB · scoped to this page</p>
                <button
                  type="button"
                  onClick={() => fontInputRef.current?.click()}
                  disabled={fontBusy}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
                >
                  {fontBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload font
                </button>
                <input
                  ref={fontInputRef}
                  type="file"
                  accept=".woff2,.woff,.ttf,font/woff2,font/woff,font/ttf"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void uploadFont(file);
                    event.target.value = '';
                  }}
                />
                {customFonts.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {customFonts.map((font) => (
                      <li key={font.family} className="flex items-center justify-between gap-2 text-[11px] text-muted">
                        <span className="truncate text-ink">{font.family} · {font.format}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const next = customFonts.filter((item) => item.family !== font.family);
                            const body = tokens.fonts?.body?.includes(font.family) ? '' : tokens.fonts?.body;
                            setTokens({ customFonts: next, fonts: { ...tokens.fonts, body } });
                          }}
                          className="inline-flex items-center gap-1 text-danger hover:underline"
                        >
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {fontError && <p className="mt-1 text-[11px] font-semibold text-danger">{fontError}</p>}
              </div>

              <label className="block text-xs font-medium text-ink">
                Corners
                <select
                  value={cornerId}
                  onChange={(event) => {
                    const choice = CORNER_CHOICES.find((item) => item.id === event.target.value);
                    setTokens({ borderRadius: { ...tokens.borderRadius, ...(choice?.radii ?? {}) } });
                  }}
                  className={inputClass}
                >
                  {CORNER_CHOICES.map((choice) => (
                    <option key={choice.id} value={choice.id}>{choice.label}</option>
                  ))}
                </select>
              </label>

              <label className="block text-xs font-medium text-ink">
                Custom CSS (this page only)
                <textarea
                  value={tokens.customCss ?? ''}
                  onChange={(event) => setTokens({ customCss: event.target.value.slice(0, 8000) })}
                  className={`${inputClass} min-h-[120px] font-mono text-[11px]`}
                  maxLength={8000}
                  placeholder={'.hero { letter-spacing: 0.02em; }'}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => cssInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-semibold text-ink hover:bg-shell"
                >
                  <Upload className="h-3.5 w-3.5" /> Upload .css
                </button>
                {(tokens.customCss ?? '') !== '' && (
                  <button
                    type="button"
                    onClick={() => setTokens({ customCss: null })}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-semibold text-danger hover:bg-shell"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Clear CSS
                  </button>
                )}
              </div>
              <input
                ref={cssInputRef}
                type="file"
                accept=".css,text/css"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) loadCssFile(file);
                  event.target.value = '';
                }}
              />
              {cssError && <p className="text-[11px] font-semibold text-danger">{cssError}</p>}
              <p className="text-[11px] text-muted">
                Custom CSS is sanitized on save (no url(), @import, or HTML). It styles the published page, not the editor chrome.
              </p>

              <button
                type="button"
                onClick={() => onChange({ designTokens: {} })}
                className="rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-muted hover:bg-shell hover:text-ink"
              >
                Reset to site theme
              </button>
            </>
          )}

          {tab === 'background' && (
            <>
              <label className="block text-xs font-medium text-ink">
                Background
                <select
                  value={settings.backgroundType ?? 'none'}
                  onChange={(event) => setSettings({ backgroundType: event.target.value as PageSettings['backgroundType'] })}
                  className={inputClass}
                >
                  <option value="none">None</option>
                  <option value="color">Solid color</option>
                  <option value="gradient">Gradient</option>
                  <option value="image">Image</option>
                </select>
              </label>
              {settings.backgroundType === 'color' && (
                <ColorField
                  label="Background color"
                  value={settings.backgroundColor ?? ''}
                  onChange={(value) => setSettings({ backgroundColor: value })}
                />
              )}
              {settings.backgroundType === 'gradient' && (
                <label className="block text-xs font-medium text-ink">
                  Gradient CSS
                  <input
                    value={settings.backgroundImage ?? ''}
                    onChange={(event) => setSettings({ backgroundImage: event.target.value })}
                    className={inputClass}
                    placeholder="linear-gradient(180deg, #ffffff, #f8fafc)"
                  />
                </label>
              )}
              {settings.backgroundType === 'image' && (
                <MediaDropzone
                  label="Background image"
                  value={settings.backgroundImage ?? ''}
                  onChange={(value) => setSettings({ backgroundImage: value })}
                  uploadUrl={uploadUrl}
                />
              )}
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
          <p className="text-[11px] text-muted">
            {saveError ? <span className="font-semibold text-danger">{saveError}</span> : 'Changes apply after Save draft / Publish.'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-ink hover:bg-shell disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save draft
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#FF8A00';
  return (
    <label className="block text-xs font-medium text-ink">
      {label}
      <span className="mt-1 flex items-center gap-2">
        <input
          type="color"
          value={safe}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-line bg-shell p-0.5"
        />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
          placeholder="#FF8A00"
        />
      </span>
    </label>
  );
}
